import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid } from '@mui/material';
// TODO: Migrate to TS
import { onCheckoutComplete } from 'components/pages/checkout/utilities';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import TopLevelSummary from 'components/common/summary/top_level_summary';
import SmsSignup from 'components/common/sms_signup';
import AddressSummary from 'components/common/summary/address_summary';
import PaymentSummary from 'components/common/summary/payment_summary';
import ContactSummary from 'components/common/summary/contact_summary';
import ShippingMethodSummary from 'components/common/summary/shipping_method_summary';
import { isFeatureEnabled, SMS } from 'utilities/features';
import { PHONE_REGEX_BY_COUNTRY } from 'constants/patterns';
import { LoadingState } from 'typescript/apps/checkout_v3/components/loading_state';
import { isOrderFree } from 'typescript/utilities/order_data';
import { COMPONENT_NAMES } from 'typescript/constants/tracking';
import { onCheckoutPageViewCombined } from 'typescript/utilities/tracking';
import PickupAddressSummary from 'components/common/summary/pickup_address_summary';

const Complete = () => {
  const { t } = useTranslation();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const {
    orderSummaryData,
    isLoading,
    error: orderSummaryError,
  } = useGetOrderSummary({
    orderNumber,
  });
  const { countryCode } = useLocaleInfo();

  // scroll to the top of the page on component render
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.ORDER_COMPLETE,
      additionalParameters: trackingData,
    });
    onCheckoutComplete(trackingData);
  }, []);

  if (isLoading) {
    return <LoadingState />;
  }

  if (orderSummaryError) {
    // TODO: temporary, consider handling this at the app level
    // an error state component will be displayed in order summary component in this scenario
    return null;
  }

  const smsSection = () =>
    !isFeatureEnabled(SMS) ||
    orderSummaryData?.bopis_fulfillment_selected ? null : (
      <>
        <Divider sx={{ my: 2 }} />
        <SmsSignup
          orderNumber={orderNumber}
          initialValue={orderSummaryData.contact_information?.phone}
          validationRegex={PHONE_REGEX_BY_COUNTRY[countryCode]}
          validationFailedMessage={t('error.validation.invalidPhone')}
          buttonText={t('checkout.sms.submit')}
          header={t('checkout.sms.title')}
          description={t('checkout.sms.description')}
          placeholder={t('checkout.sms.placeholder')}
          disclaimer={t('checkout.sms.disclaimer')}
          successMessage={t('checkout.sms.success')}
          failedMessage={t('checkout.sms.error')}
          retry={t('checkout.sms.retry')}
          url="../../api/sms_signup"
        />
      </>
    );

  const fulfillmentAddressSection = () => (
    <Grid item xs={12} md={7}>
      {orderSummaryData?.bopis_fulfillment_selected ? (
        <>
          <h3>{t('checkout.summary.address.pickupTitle')}</h3>
          <PickupAddressSummary
            pickupLocation={orderSummaryData.pickup_location_info}
          />
        </>
      ) : (
        <>
          <h3>{t('checkout.summary.address.shippingTitle')}</h3>
          <AddressSummary address={orderSummaryData.shipping_address} />
        </>
      )}
    </Grid>
  );

  const paymentAndBillingSection = () =>
    isOrderFree(orderSummaryData?.payment_due) ? null : (
      <>
        <Grid item xs={12} md={5}>
          <h3>{t('checkout.summary.address.billingTitle')}</h3>
          <AddressSummary address={orderSummaryData.billing_address} />
        </Grid>
        <Grid item xs={12} md={7}>
          <PaymentSummary payment={orderSummaryData.payment_information} />
        </Grid>
      </>
    );

  const contactSummarySection = () => (
    <Grid item xs={12} md={7}>
      <ContactSummary
        contact={
          orderSummaryData?.bopis_fulfillment_selected
            ? orderSummaryData.pickup_person_info
            : orderSummaryData.contact_information
        }
      />
    </Grid>
  );

  const deliveryMethodSection = () =>
    (orderSummaryData.bopis_fulfillment_selected ||
      !(orderSummaryData.is_warranty || orderSummaryData.no_ship)) && (
      <Grid item xs={12} md={5}>
        {orderSummaryData?.bopis_fulfillment_selected ? (
          <>
            <h3>{t('checkout.summary.deliveryMethod.title')}</h3>
            <p>{t('checkout.delivery.pickup.pickUpInStore')}</p>
          </>
        ) : (
          <ShippingMethodSummary
            shippingEstimate={orderSummaryData.shipping_estimate}
          />
        )}
      </Grid>
    );

  return (
    <>
      <Grid container>
        <TopLevelSummary
          orderData={orderSummaryData}
          orderNumber={orderNumber}
        />
      </Grid>
      {smsSection()}
      <Divider sx={{ my: 2 }} />
      <Grid container rowSpacing={2}>
        {fulfillmentAddressSection()}
        {paymentAndBillingSection()}
        {contactSummarySection()}
        {deliveryMethodSection()}
      </Grid>
    </>
  );
};

export default Complete;
